import { parsePhoneNumber } from 'awesome-phonenumber';
import { RegisterOptions } from 'react-hook-form';

/**
 * Type for validation error messages
 */
export type ValidationMessage = string | boolean | undefined;

/**
 * Interface for validation rules
 */
export interface ValidationRules {
  required?: boolean | string;
  minLength?: { value: number; message: string };
  maxLength?: { value: number; message: string };
  pattern?: { value: RegExp; message: string };
  validate?: Record<string, (value: unknown) => ValidationMessage>;
}

/**
 * Creates validation rules for a required field
 * @param message Error message to display
 * @returns Validation rule object
 */
export const required = (message = 'Detta fält måste anges'): ValidationRules => ({
  required: message
});

/**
 * Creates email validation rules
 * @param requiredMessage Message for required validation
 * @param invalidMessage Message for invalid email format
 * @returns Validation rule object
 */
export const email = (
  requiredMessage = 'E-post måste anges',
  invalidMessage = 'Ogiltig e-postadress'
): ValidationRules => ({
  required: requiredMessage,
  pattern: {
    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: invalidMessage
  }
});

/**
 * Normalizes Swedish phone numbers by adding country code if missing
 * @param value Phone number string
 * @returns Normalized phone number
 */
const normalizeSwedishPhone = (value: string): string => {
  // Remove all whitespace and common separators
  const cleaned = value.replace(/[\s\-()]/g, '');
  
  // If it starts with 07X (Swedish mobile format), add +46 and remove leading 0
  if (/^07[0-9]/.test(cleaned)) {
    return '+46' + cleaned.substring(1);
  }
  
  // If it starts with 08 (Stockholm landline), add +46 and remove leading 0
  if (/^08[0-9]/.test(cleaned)) {
    return '+46' + cleaned.substring(1);
  }
  
  // If it starts with 0 followed by other digits (other Swedish area codes), add +46 and remove leading 0
  if (/^0[1-9]/.test(cleaned)) {
    return '+46' + cleaned.substring(1);
  }
  
  // Return cleaned value if already has country code or doesn't match Swedish patterns
  return cleaned;
};

/**
 * Creates phone number validation rules
 * @param regionCode Region code for phone validation (default: 'SE' for Sweden)
 * @param requiredMessage Message for required validation
 * @param invalidMessage Message for invalid phone format
 * @returns Validation rule object
 */
export const phone = (
  regionCode = 'SE',
  requiredMessage = 'Telefonnummer måste anges',
  invalidMessage = 'Ogiltigt telefonnummer'
): ValidationRules => ({
  required: requiredMessage,
  validate: {
    isValidPhone: (value: unknown) => {
      if (!value || typeof value !== 'string') return true;
      
      // Normalize Swedish phone numbers
      const normalizedValue = regionCode === 'SE' ? normalizeSwedishPhone(value) : value;
      
      // Debug logging (remove in production)
      console.log('Phone validation:', { original: value, normalized: normalizedValue, regionCode });
      
      const phoneNumber = parsePhoneNumber(normalizedValue, { regionCode });
      console.log('Phone result:', { valid: phoneNumber.valid, possible: phoneNumber.possible, type: phoneNumber.type });
      
      return phoneNumber.valid ? true : invalidMessage;
    }
  }
});

/**
 * Creates mobile phone validation rules
 * @param regionCode Region code for phone validation (default: 'SE' for Sweden)
 * @param requiredMessage Message for required validation
 * @param invalidMessage Message for invalid mobile phone format
 * @returns Validation rule object
 */
export const mobilePhone = (
  regionCode = 'SE',
  requiredMessage = 'Mobilnummer måste anges',
  invalidMessage = 'Ogiltigt mobilnummer'
): ValidationRules => ({
  required: requiredMessage,
  validate: {
    isValidMobilePhone: (value: unknown) => {
      if (!value || typeof value !== 'string') return true;
      
      // Normalize Swedish phone numbers
      const normalizedValue = regionCode === 'SE' ? normalizeSwedishPhone(value) : value;

      const phoneNumber = parsePhoneNumber(normalizedValue, { regionCode });
      return (phoneNumber.valid && phoneNumber.type === 'mobile') ? true : invalidMessage;
    }
  }
});

/**
 * Creates boolean validation rules (must be true)
 * @param message Error message to display
 * @returns Validation rule object
 */
export const mustBeTrue = (message = 'Du måste acceptera detta'): ValidationRules => ({
  validate: {
    isTrue: (value: unknown) => value === true ? true : message
  }
});

/**
 * Creates full name validation rules (requires both first and last name)
 * @param requiredMessage Message for required validation
 * @param invalidMessage Message for invalid name format
 * @returns Validation rule object
 */
export const fullName = (
  requiredMessage = 'Namn måste anges',
  invalidMessage = 'Både för- och efternamn krävs'
): ValidationRules => ({
  required: requiredMessage,
  validate: {
    hasFirstAndLastName: (value: unknown) => {
      if (!value || typeof value !== 'string') return true;
      
      const nameParts = value.trim().split(' ');
      // Check if we have at least 2 parts and none are empty
      if (nameParts.length < 2 || nameParts.some(part => !part.trim())) {
        return invalidMessage;
      }
      
      return true;
    }
  }
});

/**
 * Conditionally applies validation rules
 * @param rules Validation rules to apply
 * @param condition Function that determines if validation should be applied
 * @returns Validation rule object or empty object if condition is false
 */
export const conditional = (
  rules: ValidationRules,
  condition: boolean
): ValidationRules => {
  return condition ? rules : {};
};

/**
 * Merges multiple validation rule objects
 * @param rules Array of validation rule objects
 * @returns Merged validation rule object
 */
export const mergeRules = (...rules: ValidationRules[]): RegisterOptions => {
  return rules.reduce((merged, rule) => {
    const result = { ...merged } as RegisterOptions;
    
    // Handle validate property specially to merge all validate functions
    if (rule.validate && result.validate) {
      result.validate = { ...result.validate, ...rule.validate };
    } else if (rule.validate) {
      result.validate = { ...rule.validate };
    }
    
    // Copy other properties, but skip validate as we've already handled it
    Object.keys(rule).forEach(key => {
      if (key !== 'validate') {
        (result as Record<string, unknown>)[key] = rule[key as keyof ValidationRules];
      }
    });
    
    return result;
  }, {} as RegisterOptions);
};

// Common validation rule combinations
export const validators = {
  required,
  email,
  phone,
  mobilePhone,
  mustBeTrue,
  fullName,
  
  // Common combinations
  requiredEmail: () => email(),
  requiredPhone: (regionCode?: string) => phone(regionCode),
  requiredMobilePhone: (regionCode?: string) => mobilePhone(regionCode),
  requiredFullName: () => fullName(),
};

export default validators;
